/**
 * Airtable interface embeds expand a record through a `detail` query param: a
 * base64url-encoded blob naming the record and the page element it was opened
 * from. Airtable renders an error instead of the record when that origin hint
 * is missing, and the element ids differ per shared view — grab a view's ids by
 * opening its share link, clicking a record, and decoding `detail` from the
 * address bar.
 */
export type AirtableEmbedIds = {
  pageId: string;
  elementId: string;
  queryContainerId: string;
};

const RECORD_ID = /^rec[a-zA-Z0-9]{14}$/;

const base64Url = (value: string) =>
  btoa(value).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

/**
 * Embed URL that loads with `recordId` expanded. Falls back to the plain embed
 * when no record — or something that isn't a record id — was asked for.
 */
export const airtableEmbedUrl = (
  embedSrc: string,
  ids: AirtableEmbedIds,
  recordId?: string | null
) => {
  if (!recordId || !RECORD_ID.test(recordId)) return embedSrc;

  const detail = base64Url(
    JSON.stringify({
      pageId: ids.pageId,
      rowId: recordId,
      showComments: false,
      queryOriginHint: {
        type: "pageElement",
        elementId: ids.elementId,
        queryContainerId: ids.queryContainerId,
      },
    })
  );

  return `${embedSrc}?detail=${detail}`;
};
